import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_CONFIG, getApiUrl, getAuthHeaders } from './config/api';
import EnhancedTreatmentModal from './components/treatments/EnhancedTreatmentModal';
import TreatmentCard from './components/treatments/TreatmentCard';
import TreatmentDetailedView from './components/treatments/TreatmentDetailedView';
import type { Doctor } from './components/calendar/types';
import { useThemedStyles } from './theme/useThemedStyles';

interface TreatmentStep {
  id: number;
  stepNumber: number;
  name: string;
  description?: string;
  cost: number;
  completed: boolean;
  completedAt?: string;
  completedBy?: string;
  createdBy?: string;
  updatedAt?: string;
}

interface Treatment {
  id: number;
  patientId: number;
  doctorId?: number | null;
  name: string;
  cost: number;
  recommendedPrice: number;
  additionalUseFee: number;
  status: string;
  isMultiStep: boolean;
  isCompleted: boolean;
  createdAt: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
  completedAt?: string;
  statusChangedAt?: string;
  statusChangedBy?: string;
  steps?: TreatmentStep[];
  totalCost: number;
  cashPaid: number;
  reservationCreditApplied: number;
  totalApplied: number;
  totalPaid: number;
  totalDue: number;
  isFullyPaid: boolean;
}

const responseError = async (response: Response, fallback: string) => {
  const body = await response.text();
  if (!body) return fallback;

  try {
    const parsed = JSON.parse(body);
    if (typeof parsed?.error === 'string') return parsed.error;
    if (typeof parsed?.detail === 'string') return parsed.detail;
    if (typeof parsed?.title === 'string') return parsed.title;
  } catch {
    return body;
  }

  return fallback;
};

export default function TreatmentsPanel({ token, patientId }: { token: string; patientId: number }) {
  const { t } = useTranslation();
  const theme = useThemedStyles();
  const [treatments, setTreatments] = useState<Treatment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTreatment, setEditingTreatment] = useState<Treatment | null>(null);
  const [selectedTreatmentId, setSelectedTreatmentId] = useState<number | null>(null);
  const [doctors, setDoctors] = useState<Doctor[]>([]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(getApiUrl(`/patients/${patientId}/treatments`), { headers: getAuthHeaders(token) });
      if (!res.ok) throw new Error('Failed to load treatments');
      const data = await res.json();
      setTreatments(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Reload when patient/token changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [token, patientId]);
  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.DOCTORS), { headers: getAuthHeaders(token) });
        if (res.ok) setDoctors(await res.json());
      } catch {}
    };
    fetchDoctors();
  }, [token]);

  const handleModalSubmit = async (payload: any) => {
    setError('');
    try {
      if (editingTreatment) {
        const body = { ...payload };
        const res = await fetch(getApiUrl(`/treatments/${editingTreatment.id}`), {
          method: 'PUT',
          headers: getAuthHeaders(token),
          body: JSON.stringify(body)
        });
        if (!res.ok) throw new Error(await responseError(res, 'Failed to update treatment'));
        setEditingTreatment(null);
      } else {
        const body = { patientId, ...payload };
        const res = await fetch(getApiUrl('/treatments'), {
          method: 'POST',
          headers: getAuthHeaders(token),
          body: JSON.stringify(body)
        });
        if (!res.ok) throw new Error(await responseError(res, 'Failed to create treatment'));
      }
      setShowModal(false);
      await load();
    } catch (e: any) {
      const message = e instanceof Error ? e.message : 'Failed to save treatment';
      setError(message);
      throw e instanceof Error ? e : new Error(message);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('treatmentsUi.confirmDeleteTreatment'))) return;
    try {
      const res = await fetch(getApiUrl(`/treatments/${id}`), { method: 'DELETE', headers: getAuthHeaders(token) });
      if (!res.ok) throw new Error('Failed to delete treatment');
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleMarkComplete = async (id: number) => {
    try {
      const res = await fetch(getApiUrl(`/treatments/${id}/complete`), {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({})
      });
      if (!res.ok) throw new Error('Failed to mark treatment complete');
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleMarkIncomplete = async (id: number) => {
    try {
      const res = await fetch(getApiUrl(`/treatments/${id}/incomplete`), {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({})
      });
      if (!res.ok) throw new Error('Failed to mark treatment incomplete');
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      const res = await fetch(getApiUrl(`/treatments/${id}/status`), {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ status: newStatus })
      });
      if (!res.ok) throw new Error('Failed to update treatment status');
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleMarkStepComplete = async (treatmentId: number, stepId: number) => {
    try {
      const res = await fetch(getApiUrl(`/treatments/${treatmentId}/steps/${stepId}/complete`), {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({})
      });
      if (!res.ok) throw new Error('Failed to mark step complete');
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleMarkStepIncomplete = async (treatmentId: number, stepId: number) => {
    try {
      const res = await fetch(getApiUrl(`/treatments/${treatmentId}/steps/${stepId}/incomplete`), {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({})
      });
      if (!res.ok) throw new Error('Failed to mark step incomplete');
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleDeleteStep = async (treatmentId: number, stepId: number) => {
    if (!window.confirm(t('treatmentsUi.confirmDeleteStep'))) return;
    try {
      const res = await fetch(getApiUrl(`/treatments/${treatmentId}/steps/${stepId}`), {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });
      if (!res.ok) throw new Error('Failed to delete step');
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const selectedTreatment = selectedTreatmentId
    ? treatments.find(t => t.id === selectedTreatmentId)
    : null;

  if (loading) return <div style={{ padding: 16, color: theme.text }}>{t('treatmentsUi.loading')}</div>;

  return (
    <div style={{ ...theme.card, padding: 16, borderRadius: 10 }}>
      <div className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('treatmentsUi.title')}
          </h3>
          <div style={{ fontSize: 12, color: theme.muted }}>
            {t('treatmentsUi.count', { count: treatments.length })}
          </div>
        </div>
        <button
          onClick={() => {
            setEditingTreatment(null);
            setShowModal(true);
          }}
          style={{
            padding: '10px 16px',
            background: theme.primary,
            color: theme.primaryText,
            border: `1px solid ${theme.primary}`,
            borderRadius: 6,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 13
          }}
        >
          + {t('treatmentsUi.newTreatment')}
        </button>
      </div>

      {error && (
        <div style={{ padding: 12, background: '#7f1d1d', color: '#fca5a5', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
          {error}
        </div>
      )}

      {treatments.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: theme.muted, background: theme.inputBg, borderRadius: 8 }}>
          <div style={{ marginBottom: 8 }}>{t('treatmentsUi.none')}</div>
          <button
            onClick={() => setShowModal(true)}
            style={{
              marginTop: 12,
              padding: '8px 16px',
              background: theme.primary,
              color: theme.primaryText,
              border: `1px solid ${theme.primary}`,
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600
            }}
          >
            {t('treatmentsUi.createFirst')}
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {treatments.map((treatment) => (
            <div
              key={treatment.id}
              style={{
                opacity: treatment.status === 'archived' ? 0.7 : 1,
                position: 'relative'
              }}
            >
              <div className="responsive-row" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 8 }}>
                <button
                  onClick={() => {
                    setEditingTreatment(treatment);
                    setShowModal(true);
                  }}
                  style={{
                    padding: '6px 10px',
                    background: '#0ea5e9',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 600
                  }}
                >
                  {t('treatmentsUi.edit')}
                </button>
                <button
                  onClick={() => handleDelete(treatment.id)}
                  style={{
                    padding: '6px 10px',
                    background: '#ef4444',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 600
                  }}
                >
                  {t('treatmentsUi.delete')}
                </button>
              </div>
              <TreatmentCard
                id={treatment.id}
                name={treatment.name}
                cost={treatment.cost}
                totalCost={treatment.totalCost}
                cashPaid={treatment.cashPaid ?? treatment.totalPaid}
                reservationCreditApplied={treatment.reservationCreditApplied ?? 0}
                totalPaid={treatment.totalApplied ?? treatment.totalPaid}
                totalDue={treatment.totalDue}
                status={treatment.status}
                isCompleted={treatment.isCompleted}
                isFullyPaid={treatment.isFullyPaid}
                isMultiStep={treatment.isMultiStep}
                steps={treatment.steps}
                createdAt={treatment.createdAt}
                createdBy={treatment.createdBy}
                completedAt={treatment.completedAt}
                statusChangedAt={treatment.statusChangedAt}
                statusChangedBy={treatment.statusChangedBy}
                onMarkComplete={() => handleMarkComplete(treatment.id)}
                onMarkIncomplete={() => handleMarkIncomplete(treatment.id)}
                onStatusChange={(newStatus) => handleStatusChange(treatment.id, newStatus)}
                onViewSteps={(id) => setSelectedTreatmentId(id)}
              />
            </div>
          ))}
        </div>
      )}

      <EnhancedTreatmentModal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingTreatment(null);
        }}
        onSubmit={handleModalSubmit}
        doctors={doctors}
        isEditing={!!editingTreatment}
        initial={editingTreatment ? {
          name: editingTreatment.name,
          cost: editingTreatment.cost.toString(),
          recommendedPrice: (editingTreatment.recommendedPrice ?? 0).toString(),
          additionalUseFee: (editingTreatment.additionalUseFee ?? 0).toString(),
          doctorId: editingTreatment.doctorId?.toString() || '',
          isMultiStep: editingTreatment.isMultiStep,
          steps: (editingTreatment.steps || []).map(s => ({
            name: s.name,
            description: s.description || '',
            cost: s.cost.toString()
          })),
          installments: [],
          createdBy: editingTreatment.createdBy
        } : undefined}
      />

      {selectedTreatment && (
        <TreatmentDetailedView
          open={selectedTreatmentId !== null}
          onClose={() => setSelectedTreatmentId(null)}
          treatmentId={selectedTreatment.id}
          treatmentName={selectedTreatment.name}
          totalCost={selectedTreatment.totalCost}
          cashPaid={selectedTreatment.cashPaid ?? selectedTreatment.totalPaid}
          reservationCreditApplied={selectedTreatment.reservationCreditApplied ?? 0}
          totalPaid={selectedTreatment.totalApplied ?? selectedTreatment.totalPaid}
          totalDue={selectedTreatment.totalDue}
          paymentPercent={selectedTreatment.totalCost > 0 ? ((selectedTreatment.totalApplied ?? selectedTreatment.totalPaid) / selectedTreatment.totalCost) * 100 : 0}
          completedSteps={selectedTreatment.steps?.filter(s => s.completed).length || 0}
          totalSteps={selectedTreatment.steps?.length || 0}
          steps={selectedTreatment.steps || []}
          status={selectedTreatment.status}
          isCompleted={selectedTreatment.isCompleted}
          isMultiStep={selectedTreatment.isMultiStep}
          onMarkStepComplete={selectedTreatment.isMultiStep ? handleMarkStepComplete : undefined}
          onMarkStepIncomplete={selectedTreatment.isMultiStep ? handleMarkStepIncomplete : undefined}
          onDeleteStep={selectedTreatment.isMultiStep ? handleDeleteStep : undefined}
          onStatusChange={(newStatus) => handleStatusChange(selectedTreatment.id, newStatus)}
        />
      )}
    </div>
  );
}
